import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import useWebSocketContext from './useWebSocketContext';
import { setKbStatus } from '../../store/slices/kbSlice';
import { incrementProcessingCount } from '../../store/slices/fileProcessingSlice';
import { userMessage } from '../../store/slices/chatSlice';

const useFileUpload = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { session_id, user_id } = useSelector((state: RootState) => state.session);
    const activeChatGroup = useSelector((state: RootState) => state.chatHistory.activeChatGroup);
    const { sendBinaryToWs, sendToWs } = useWebSocketContext();
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    const getMethodForFileName = (fileName: string): string => {
        console.log(`Determining method for file: ${fileName}`);
        const upperName = fileName.toUpperCase();
        if (upperName.includes('MEL')) return 'load_mel_application';
        if (['AACOMAS', 'AMCAS'].some(keyword => upperName.includes(keyword))) return 'load_sgu_application';
        if (upperName.includes('IL_PR')) return 'generate_fcic_fnol';
        if (upperName.includes('FNOL')) return 'process_fcic_claim';
        return 'load_docs';
    };

    const sendMetadataAndBinaryData = (metadata: string, binaryData: Blob) => {
        const delimiter = "\0";
        const combinedMessage = new Blob([metadata, delimiter, binaryData]);
        sendBinaryToWs(combinedMessage);
    };

    const processFiles = (files: FileList) => {
        console.log(`Processing ${files.length} file(s)`);
        dispatch(setKbStatus('loading'));

        // Check if we have valid session data
        if (!session_id || !user_id) {
            console.error('No valid session data available');
            return;
        }

        const supportedTypes = new Map([
            ['application/pdf', 'pdf'],
            ['text/plain', 'txt'],
            ['text/csv', 'csv'],
            ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'docx'],
            ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx'],
            ['application/vnd.ms-excel.sheet.macroEnabled.12', 'xlsm'],
            ['application/vnd.ms-excel', 'xls']
        ]);

        const supportedExtensions = ['.pdf', '.txt', '.csv', '.docx', '.xlsx', '.xlsm', '.xls'];

        const validFiles = Array.from(files).filter((file: File) => {
            // Check by MIME type first
            if (supportedTypes.has(file.type)) {
                return true;
            }
            // Fallback: Check by file extension if MIME type is not recognized
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            return supportedExtensions.includes(fileExtension);
        });

        if (validFiles.length === 0) {
            console.error('No supported files found. Please upload PDF, TXT, CSV, DOCX, XLSX, XLSM, or XLS files.');
            return;
        }

        validFiles.forEach((file: File) => {
            console.log(`Selected file: ${file.name}`);
            // Dispatch user message immediately for file upload
            dispatch(userMessage(`Uploaded file: ${file.name}`));
            dispatch(incrementProcessingCount());

            // Get the file type from the supported types map, or fallback to extension
            let fileType = supportedTypes.get(file.type);
            if (!fileType) {
                // Fallback: determine type from file extension
                const fileExtension = file.name.split('.').pop()?.toLowerCase();
                fileType = fileExtension || 'pdf';
            }
            console.log('fileType', fileType);
            const methodToSend = getMethodForFileName(file.name);
            
            const params: {
                agent: string;
                session_id: string;
                user_id: string;
                doc_name: string;
                file_type: string;
                chat_group_id?: string;
            } = {
                agent: 'keai',
                session_id: session_id,
                user_id: user_id,
                doc_name: file.name,
                file_type: fileType 
            };

            if (activeChatGroup) {
                params.chat_group_id = activeChatGroup;
            }
            
            const metadata = JSON.stringify({
                method: methodToSend,
                params: params
            });
            
            console.log(`Sending file to backend with method: ${methodToSend}`);
            sendMetadataAndBinaryData(metadata, file);
            console.log(`File sent`);
        });
    };

    const processUrl = (url: string) => {
        console.log(`Valid URL detected: ${url}, sending to backend...`);
        
        // Check if we have valid session data
        if (!session_id || !user_id) {
            console.error('No valid session data available');
            return;
        }
        
        dispatch(setKbStatus('loading'));
        dispatch(incrementProcessingCount());

        console.log(`Sending URL to backend: ${url}`);
        const urlData = JSON.stringify({
            method: 'load_url',
            params: {
                agent: 'keai',
                session_id: session_id,
                user_id: user_id,
                url: url,
                file_type: 'url'
            }
        });

        sendToWs(urlData);
    };

    const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        // CRITICAL: Prevent default FIRST to stop Safari from navigating to PDF
        event.preventDefault();
        event.stopPropagation();

        // Reset drag state
        dragCounterRef.current = 0;
        setIsDragging(false);

        // Try dataTransfer.files first (works in most browsers)
        const files = event.dataTransfer.files;

        if (files && files.length > 0) {
            processFiles(files);
            return;
        }

        // Fallback: Safari fix for downloads section
        // Safari doesn't populate dataTransfer.files when dragging from downloads
        const items = event.dataTransfer.items;

        if (items.length === 0) {
            console.error('No items dropped');
            return;
        }

        // Try to get files from items
        const filesArray: File[] = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                    filesArray.push(file);
                }
            }
        }

        if (filesArray.length > 0) {
            // Create a FileList-like object
            const fileList = Object.assign(filesArray, {
                item: (index: number) => filesArray[index] || null
            }) as unknown as FileList;
            processFiles(fileList);
            return;
        }

        // If no files found, check for URLs
        for (const item of items) {
            if (item.kind === 'string' && item.type.match('^text/plain')) {
                item.getAsString((text) => {
                    if (isValidUrl(text)) {
                        processUrl(text);
                    } else {
                        console.error('Dropped text is not a valid URL');
                    }
                });
                break;
            }
        }
    };

    const isValidUrl = (urlString: string): boolean => {
        try {
            new URL(urlString);
            return true;
        } catch {
            return false;
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) {
            console.error('Error reading files');
            return;
        }
        processFiles(files);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        
        // Only hide the drag state when we've actually left the drop zone
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        // Safari fix: Explicitly set dropEffect to prevent browser from opening the file
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    return {
        isDragging,
        handleFileDrop,
        handleFileSelect,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
    };
};

export default useFileUpload; 